import {
  CircleAlertIcon,
  CircleCheckIcon,
  CodeBlock,
  ComposableTable,
  TableCell,
  Typography,
  ZeroState,
  useTheme,
  useToast,
} from "@codat/orchard-ui";
import { DataConnection } from "api/public";
import { DataConnectionError } from "api/queries/useGetCompanyInfoQuery";
import React from "react";
import { formatDateAndTimeWithShortString } from "utils/formatDate";

import styles from "./ConnectionErrorsTable.module.scss";

export interface ConnectionErrorsTableProps {
  isSkeleton: boolean;
  dataConnections: Pick<
    DataConnection,
    "id" | "platformName" | "dataConnectionErrors" | "sourceType"
  >[];
}

type ConnectionWithError = {
  error: DataConnectionError;
  id: DataConnection["id"];
  platformName: DataConnection["platformName"];
  sourceType: DataConnection["sourceType"];
};

export const ConnectionErrorsTable: React.FC<ConnectionErrorsTableProps> = ({
  dataConnections,
  isSkeleton,
}) => {
  const { addToast } = useToast();
  const { colors } = useTheme();

  const errorsForConnections = dataConnections.reduce(
    (acc, dataConnection): ConnectionWithError[] => {
      return [
        ...acc,
        ...(dataConnection.dataConnectionErrors?.map((error) => {
          return {
            id: dataConnection.id,
            sourceType: dataConnection.sourceType,
            platformName: dataConnection.platformName,
            error,
          };
        }) ?? []),
      ];
    },
    [] as ConnectionWithError[]
  );

  if (!isSkeleton && errorsForConnections.length === 0) {
    return (
      <ZeroState
        data-testid="connection-errors-zero-state"
        title="No errors"
        description={`If any errors occur at the connection level, either during the linking process or once the connection is linked, they will appear here in JSON format.`}
      />
    );
  }
  return (
    <div data-testid="connection-errors-table">
      <ComposableTable
        initialNestingOpen={false}
        id="connection-errors-table"
        className={styles.tableContainer}
        isSkeleton={isSkeleton}
      >
        <ComposableTable.Head rowId="header">
          <ComposableTable.Header
            label="Error"
            className={styles.connectionColumn}
          />
          <ComposableTable.Header label="Date" />
          <ComposableTable.Header label="Source type" />
        </ComposableTable.Head>
        <ComposableTable.Body>
          {errorsForConnections.map((errorForConnection, i) => (
            <ComposableTable.Row
              rowId={`row1-${i}`}
              key={errorForConnection.id}
              data-testid="connection-error-row"
              className={
                errorForConnection.error.status === "Active"
                  ? styles.activeRow
                  : ""
              }
            >
              <TableCell className={styles.connectionCell}>
                {errorForConnection.error.status === "Active" ? (
                  <CircleAlertIcon
                    fillColor={colors["color-ui-warning-mid"]}
                    className={styles.errorIcon}
                  />
                ) : (
                  <CircleCheckIcon
                    fillColor={colors["color-ui-interface-60"]}
                    className={styles.errorIcon}
                  />
                )}
                <div className={styles.cellContent}>
                  <Typography as="p" variant="heading_xs">
                    {errorForConnection.platformName}
                  </Typography>
                  <div className={styles.subtext}>
                    <Typography
                      as="span"
                      variant="body_sm"
                      className={styles.tableText}
                    >
                      {errorForConnection.error.errorMessage}
                    </Typography>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Typography
                  as="p"
                  variant="body_sm"
                  className={styles.tableText}
                >
                  {formatDateAndTimeWithShortString(
                    errorForConnection.error.erroredOnUtc ?? "",
                    true
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  as="p"
                  variant="body_sm"
                  className={styles.tableText}
                >
                  {errorForConnection.sourceType}
                </Typography>
              </TableCell>
              <ComposableTable.Row rowId={`row2-${i}`}>
                <TableCell colSpan={3} className={styles.codeRow}>
                  <CodeBlock
                    copyOptions={{
                      onSuccess: () =>
                        addToast({
                          message: "Copied error code to clipboard",
                          variant: "success",
                        }),
                      onFailure: () =>
                        addToast({
                          message: "Failed to copy error code to clipboard",
                          variant: "success",
                        }),
                    }}
                    downloadOptions={{
                      filename: `error-${errorForConnection.error.erroredOnUtc}.json`,
                      onFailure: () => {
                        alert("fail");
                      },
                    }}
                    code={errorForConnection.error}
                  />
                </TableCell>
              </ComposableTable.Row>
            </ComposableTable.Row>
          ))}
        </ComposableTable.Body>
      </ComposableTable>
    </div>
  );
};
